import { grpcClientFactory } from './grpcClientFactory';
import { demoProjectsService } from './demoProjectsService';
// Define types locally to avoid Node.js imports
export var ExportFormat;
(function (ExportFormat) {
    ExportFormat[ExportFormat["EXPORT_FORMAT_UNSPECIFIED"] = 0] = "EXPORT_FORMAT_UNSPECIFIED";
    ExportFormat[ExportFormat["EXPORT_FORMAT_TIF"] = 1] = "EXPORT_FORMAT_TIF";
    ExportFormat[ExportFormat["EXPORT_FORMAT_PNG"] = 2] = "EXPORT_FORMAT_PNG";
    ExportFormat[ExportFormat["EXPORT_FORMAT_KML"] = 3] = "EXPORT_FORMAT_KML";
    ExportFormat[ExportFormat["EXPORT_FORMAT_SHAPEFILE"] = 4] = "EXPORT_FORMAT_SHAPEFILE";
    ExportFormat[ExportFormat["EXPORT_FORMAT_GEOJSON"] = 5] = "EXPORT_FORMAT_GEOJSON";
    ExportFormat[ExportFormat["EXPORT_FORMAT_SVG"] = 6] = "EXPORT_FORMAT_SVG";
    ExportFormat[ExportFormat["EXPORT_FORMAT_DXF"] = 7] = "EXPORT_FORMAT_DXF";
    ExportFormat[ExportFormat["EXPORT_FORMAT_PNG_MAP"] = 8] = "EXPORT_FORMAT_PNG_MAP";
})(ExportFormat || (ExportFormat = {}));
class ArtifactExportService {
    constructor() {
        this.client = null;
        // Client will be created lazily when needed
    }
    /**
     * Get or create the authenticated client
     */
    getClient() {
        if (!this.client) {
            // Create authenticated client for user-owned artifact operations
            this.client = grpcClientFactory.createAuthenticatedUserProjectsClient({
                enableCompression: true // Enable compression for potentially large responses
            });
        }
        return this.client;
    }
    /**
     * Clear the client (useful when user logs out)
     */
    clearClient() {
        this.client = null;
    }
    /**
     * Export an artifact to a specific format
     */
    async exportArtifact(artifactId, format, options = {}, isDemoProject = false) {
        // For demo projects, use the demo projects service
        if (isDemoProject) {
            return demoProjectsService.exportArtifact(artifactId, format);
        }
        return new Promise((resolve, reject) => {
            try {
                // Check authentication before proceeding
                const client = this.getClient();
                // Create request using proto namespace (following existing patterns)
                const ExportArtifactRequestProto = proto.landscope.mapviewer.v1.ExportArtifactRequest;
                const ExportOptionsProto = proto.landscope.mapviewer.v1.ExportOptions;
                const request = new ExportArtifactRequestProto();
                request.setArtifactId(artifactId);
                request.setFormat(format);
                // Create options message (reserved for future use)
                const exportOptions = new ExportOptionsProto();
                request.setOptions(exportOptions);
                client.exportArtifact(request, {}, (err, response) => {
                    if (err) {
                        console.error('Export artifact error:', err);
                        // Handle specific gRPC error codes
                        let errorMessage = 'Failed to export artifact';
                        if (err.code === 7) { // PERMISSION_DENIED
                            errorMessage = 'You do not have permission to export this artifact';
                        }
                        else if (err.code === 5) { // NOT_FOUND
                            errorMessage = 'Artifact not found';
                        }
                        else if (err.code === 12) { // UNIMPLEMENTED
                            errorMessage = `Export format ${this.getFormatName(format)} is not yet supported`;
                        }
                        else if (err.message) {
                            errorMessage = err.message;
                        }
                        reject(new Error(errorMessage));
                        return;
                    }
                    if (response) {
                        const result = {
                            success: response.getSuccess(),
                            message: response.getMessage() || '',
                            downloadUrl: response.getDownloadUrl() || undefined,
                            downloadId: response.getDownloadId() || undefined
                        };
                        resolve(result);
                    }
                    else {
                        reject(new Error('No response from export service'));
                    }
                });
            }
            catch (error) {
                console.error('Export artifact request error:', error);
                // Handle authentication errors gracefully
                if (error instanceof Error && error.message.includes('No authentication tokens')) {
                    reject(new Error('Please log in to export artifacts'));
                }
                else {
                    reject(error);
                }
            }
        });
    }
    /**
     * Export a user-folder artifact (path / polygon / placemark / measurement
     * the user drew) to a format that requires server-side conversion:
     * SHAPEFILE / SVG / DXF. KML and GeoJSON for these stay on the client —
     * see utils/userArtifactExport.ts.
     */
    async exportUserArtifact(artifactId, format) {
        return new Promise((resolve, reject) => {
            try {
                const client = this.getClient();
                const ExportUserArtifactRequestProto = proto.landscope.mapviewer.v1.ExportUserArtifactRequest;
                const request = new ExportUserArtifactRequestProto();
                request.setArtifactId(artifactId);
                request.setFormat(format);
                client.exportUserArtifact(request, {}, (err, response) => {
                    if (err) {
                        let errorMessage = 'Failed to export artifact';
                        if (err.code === 7) {
                            errorMessage = 'You do not have permission to export this artifact';
                        }
                        else if (err.code === 5) {
                            errorMessage = 'Artifact not found';
                        }
                        else if (err.code === 12) {
                            errorMessage = `Export format ${this.getFormatName(format)} is not yet supported`;
                        }
                        else if (err.message) {
                            errorMessage = err.message;
                        }
                        reject(new Error(errorMessage));
                        return;
                    }
                    if (!response) {
                        reject(new Error('No response from export service'));
                        return;
                    }
                    resolve({
                        success: response.getSuccess(),
                        message: response.getMessage() || '',
                        downloadUrl: response.getDownloadUrl() || undefined,
                        filename: response.getFilename() || undefined,
                    });
                });
            }
            catch (error) {
                if (error instanceof Error && error.message.includes('No authentication tokens')) {
                    reject(new Error('Please log in to export artifacts'));
                }
                else {
                    reject(error);
                }
            }
        });
    }
    /**
     * Export an entire folder (every artifact in it and its descendant
     * subfolders) to a format that requires server-side conversion:
     * SHAPEFILE / SVG / DXF. KML / GeoJSON / GPX are assembled on the client —
     * see utils/userArtifactExport.ts (exportFolder).
     */
    async exportUserFolder(folderId, format) {
        return new Promise((resolve, reject) => {
            try {
                const client = this.getClient();
                const ExportUserFolderRequestProto = proto.landscope.mapviewer.v1.ExportUserFolderRequest;
                const request = new ExportUserFolderRequestProto();
                request.setFolderId(folderId);
                request.setFormat(format);
                client.exportUserFolder(request, {}, (err, response) => {
                    if (err) {
                        let errorMessage = 'Failed to export folder';
                        if (err.code === 7) {
                            errorMessage = 'You do not have permission to export this folder';
                        }
                        else if (err.code === 5) {
                            errorMessage = 'Folder not found';
                        }
                        else if (err.code === 12) {
                            errorMessage = `Export format ${this.getFormatName(format)} is not yet supported`;
                        }
                        else if (err.message) {
                            errorMessage = err.message;
                        }
                        reject(new Error(errorMessage));
                        return;
                    }
                    if (!response) {
                        reject(new Error('No response from export service'));
                        return;
                    }
                    resolve({
                        success: response.getSuccess(),
                        message: response.getMessage() || '',
                        downloadUrl: response.getDownloadUrl() || undefined,
                        filename: response.getFilename() || undefined,
                    });
                });
            }
            catch (error) {
                if (error instanceof Error && error.message.includes('No authentication tokens')) {
                    reject(new Error('Please log in to export folders'));
                }
                else {
                    reject(error);
                }
            }
        });
    }
    /**
     * Get supported export formats for an artifact
     */
    async getSupportedExportFormats(projectId, layerName) {
        return new Promise((resolve, reject) => {
            try {
                // Check authentication before proceeding
                const client = this.getClient();
                // Create request using proto namespace (following existing patterns)
                const request = new proto.landscope.mapviewer.v1.GetSupportedExportFormatsRequest();
                request.setProjectId(projectId);
                request.setLayerName(layerName);
                client.getSupportedExportFormats(request, {}, (err, response) => {
                    if (err) {
                        console.error('❌ Get supported formats gRPC error:', err);
                        console.error('Error details:', {
                            code: err.code,
                            message: err.message,
                            details: err.details
                        });
                        let errorMessage = 'Failed to get supported formats';
                        if (err.code === 7) { // PERMISSION_DENIED
                            errorMessage = 'You do not have permission to access this artifact';
                        }
                        else if (err.code === 5) { // NOT_FOUND
                            errorMessage = 'Artifact not found';
                        }
                        else if (err.message) {
                            errorMessage = err.message;
                        }
                        reject(new Error(errorMessage));
                        return;
                    }
                    if (response) {
                        const availableFormats = response.getAvailableFormatsList() || [];
                        // Convert protobuf available formats to our interface
                        const convertedAvailableFormats = availableFormats.map((af) => ({
                            format: af.getFormat(),
                            artifactId: af.getArtifactId() || ''
                        }));
                        const result = {
                            availableFormats: convertedAvailableFormats
                        };
                        resolve(result);
                    }
                    else {
                        console.error('❌ No response from supported formats service');
                        reject(new Error('No response from supported formats service'));
                    }
                });
            }
            catch (error) {
                console.error('Get supported formats request error:', error);
                // Handle authentication errors gracefully
                if (error instanceof Error && error.message.includes('No authentication tokens')) {
                    reject(new Error('Please log in to access export formats'));
                }
                else {
                    reject(error);
                }
            }
        });
    }
    /**
     * Convert export format enum to human-readable name
     */
    getFormatName(format) {
        switch (format) {
            case ExportFormat.EXPORT_FORMAT_UNSPECIFIED:
                return 'Unknown'; // Should not be displayed
            case ExportFormat.EXPORT_FORMAT_TIF:
                return 'GeoTIFF';
            case ExportFormat.EXPORT_FORMAT_PNG:
                // Globe-aligned PNG — niche output (reprojected for use on a 3D
                // sphere). The user-facing menu filters this out; the label is
                // retained for status/error messages where the format may still
                // be referenced programmatically.
                return 'PNG (3D-aligned)';
            case ExportFormat.EXPORT_FORMAT_PNG_MAP:
                return 'PNG';
            case ExportFormat.EXPORT_FORMAT_KML:
                return 'KML';
            case ExportFormat.EXPORT_FORMAT_SHAPEFILE:
                return 'Shapefile';
            case ExportFormat.EXPORT_FORMAT_GEOJSON:
                return 'GeoJSON';
            case ExportFormat.EXPORT_FORMAT_SVG:
                return 'SVG';
            case ExportFormat.EXPORT_FORMAT_DXF:
                return 'DXF';
            default:
                return 'Unknown';
        }
    }
    /**
     * Get format icon based on format type
     */
    getFormatIcon(format) {
        switch (format) {
            case ExportFormat.EXPORT_FORMAT_UNSPECIFIED:
                return 'file';
            case ExportFormat.EXPORT_FORMAT_TIF:
            case ExportFormat.EXPORT_FORMAT_PNG:
            case ExportFormat.EXPORT_FORMAT_PNG_MAP:
                return 'image';
            case ExportFormat.EXPORT_FORMAT_KML:
            case ExportFormat.EXPORT_FORMAT_GEOJSON:
            case ExportFormat.EXPORT_FORMAT_SHAPEFILE:
                return 'map';
            case ExportFormat.EXPORT_FORMAT_SVG:
                return 'pencil';
            case ExportFormat.EXPORT_FORMAT_DXF:
                return 'layers';
            default:
                return 'file';
        }
    }
    /**
     * Initiate download from export result
     */
    initiateDownload(exportResult) {
        if (!exportResult.success || !exportResult.downloadUrl) {
            throw new Error('Invalid export result for download');
        }
        try {
            // Create temporary link element and trigger download
            const link = document.createElement('a');
            link.href = exportResult.downloadUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            // Add to DOM, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        catch (error) {
            console.error('Error initiating download:', error);
            throw new Error('Failed to initiate download');
        }
    }
}
// Export singleton instance
export const artifactExportService = new ArtifactExportService();
// ExportFormat is already exported as enum above
